import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onActiveUserChanged, readActiveUserID, scopedStorageKey } from "@/lib/user-scope";

type Theme = "light" | "dark";
const Ctx = createContext<{ theme: Theme; toggle: () => void }>({ theme: "dark", toggle: () => {} });
const THEME_STORAGE_KEY = "nebula-theme";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>("dark");
  const [activeUserID, setActiveUserID] = useState<string>(() => readActiveUserID());

  useEffect(() => {
    return onActiveUserChanged((nextUserID) => {
      setActiveUserID(nextUserID);
    });
  }, []);

  // Hydrate scoped theme whenever the active user changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(scopedStorageKey(THEME_STORAGE_KEY, activeUserID)) as Theme | null;
    if (stored === "light" || stored === "dark") {
      setTheme(stored);
      return;
    }
    setTheme("dark");
  }, [activeUserID]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.classList.toggle("light", theme === "light");
    try {
      localStorage.setItem(scopedStorageKey(THEME_STORAGE_KEY, activeUserID), theme);
    } catch {
      // ignore storage errors
    }
  }, [theme, activeUserID]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "j") return;
      event.preventDefault();
      setTheme((current) => (current === "dark" ? "light" : "dark"));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <Ctx.Provider value={{ theme, toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")) }}>
      {children}
    </Ctx.Provider>
  );
}

export const useTheme = () => useContext(Ctx);
